import { createLoadingIndicator } from './createLoadingIndicator';

/**
 * Sets HTMX attributes on a trigger element.
 * @param {HTMLElement} trigger - The trigger element.
 * @param {string} targetComponent - The target component name.
 */
export const setHxAttrs = (
    trigger: HTMLElement,
    targetComponent: string,
): void => {
    const dataTarget =
        trigger.getAttribute(`data-${targetComponent}-target`) || '';
    const targetId = dataTarget.replace('#', '');

    trigger.setAttribute(
        'hx-get',
        trigger.getAttribute(`data-${targetComponent}-hx-url`) || '',
    );
    trigger.setAttribute(
        'hx-swap',
        trigger.getAttribute('hx-swap') || 'innerHtml',
    );
    trigger.setAttribute(
        'hx-target',
        trigger.getAttribute('hx-target') || `#${targetId}-content`,
    );
    trigger.setAttribute(
        'hx-indicator',
        trigger.getAttribute('hx-indicator') || `#${targetId}-indicator`,
    );

    // HTMX has already scanned this scope by the time Tako adds hx-* attrs
    // (true on both initial load and partial swaps), so the trigger must be
    // re-processed for HTMX to register its handlers. Idempotent + a no-op
    // if HTMX isn't on the page.
    window.htmx?.process(trigger);
};

/**
 * Creates or retrieves an HTMX target element.
 * @param {HTMLElement} trigger - The trigger element.
 * @returns {HTMLElement} The HTMX target element.
 */
export const createHxTarget = (trigger: HTMLElement): HTMLElement => {
    const hxTargetId = trigger.getAttribute('hx-target') || '';
    let hxTarget = document.querySelector<HTMLElement>(hxTargetId);
    if (!hxTarget) {
        hxTarget = document.createElement('div');
        hxTarget.setAttribute('id', hxTargetId.replace('#', ''));
        hxTarget.setAttribute('data-htmx-content', 'true');
    }
    return hxTarget;
};

/**
 * Creates or retrieves an HTMX loading indicator element.
 * @param {HTMLElement} trigger - The trigger element.
 * @param {string} [size='sm'] - The size of the loading indicator.
 * @returns {HTMLElement} The HTMX loading indicator element.
 */
export const createHxIndicator = (
    trigger: HTMLElement,
    size: string = 'sm',
): HTMLElement => {
    const hxIndicatorId = trigger.getAttribute('hx-indicator') || '';
    let hxIndicatorWrapper = document.querySelector<HTMLElement>(hxIndicatorId);
    if (!hxIndicatorWrapper) {
        hxIndicatorWrapper = document.createElement('div');
        hxIndicatorWrapper.setAttribute('id', hxIndicatorId.replace('#', ''));
    }
    const hxIndicator = createLoadingIndicator(size);
    hxIndicatorWrapper.prepend(hxIndicator);
    hxIndicatorWrapper.classList.add('htmx-indicator', 'htmx-block-indicator');
    return hxIndicatorWrapper;
};
